// ---------------------------------------------------------------------------
// Help content definitions — keyed by topic ID
// ---------------------------------------------------------------------------

export interface HelpSection {
  heading: string;
  body: string;              // supports **bold** markdown
  formula?: string;          // displayed in monospace block
  note?: string;             // callout box
  table?: { headers: string[]; rows: string[][] };
}

export interface HelpTopic {
  id: string;
  title: string;
  icon: string;              // emoji or character
  brief: string;             // one-liner subtitle
  sections: HelpSection[];
}

const HELP_TOPICS: Record<string, HelpTopic> = {

  // =========================================================================
  // 1. Significant Figures & Display Precision
  // =========================================================================
  'significant-figures': {
    id: 'significant-figures',
    title: 'Significant Figures & Display Precision',
    icon: '#',
    brief: 'Why 3.000 rotatable bonds is wrong and what Adroit does about it',
    sections: [
      {
        heading: 'The Principle',
        body: '**Never display more precision than the measurement technique can deliver.** Showing a molecular weight as 180.15888 Da implies sub-millidalton accuracy, which is meaningless for most applications. Showing rotatable bonds as 3.000 implies a fractional bond count, which is physically absurd.',
      },
      {
        heading: 'Display Rules in Adroit',
        body: 'Every property type has a precision rule enforced at the rendering layer. These are not arbitrary — they reflect the resolution of the underlying calculation or assay.',
        table: {
          headers: ['Property', 'Precision', 'Example', 'Rationale'],
          rows: [
            ['Ring count, HBD, HBA, RotBonds', 'Integer', '3', 'Discrete counts — no decimals ever'],
            ['Molecular Weight', '2 decimal places', '180.16 Da', 'Monoisotopic mass resolution'],
            ['LogP, LogS', '2 decimal places', '1.31', 'Prediction error ~0.5 log units'],
            ['TPSA', '1 decimal place', '63.6 \u00C5\u00B2', 'Topological — angstrom resolution'],
            ['IC50, EC50, Ki', '3 significant figures', '4.52 nM', 'Assay CV typically 20-50%'],
            ['pKa', '2 decimal places', '4.75', 'Measurement precision ~0.1 units'],
            ['HOMO/LUMO (Hartree)', '6 decimal places', '-0.251347 E\u2095', 'SCF convergence threshold'],
            ['HOMO/LUMO Gap (eV)', '3 decimal places', '6.823 eV', 'Converted from Hartree'],
            ['Binding affinity (kcal/mol)', '1 decimal place', '-7.5 kcal/mol', 'Vina scoring precision'],
            ['Extraction ratio', '3 decimal places', '0.040', 'Derived from CLint model'],
          ],
        },
      },
      {
        heading: 'The Femtomolar Story',
        body: 'A paper once reported a binding constant of 1.234567890 \u00D7 10\u207B\u00B9\u2075 M — ten significant figures at the femtomolar level. This implies the experimenters could distinguish concentrations differing by one part in ten billion, which at femtomolar concentrations corresponds to **counting individual molecules in the assay well**. The reported precision far exceeded what the instrument could physically measure.',
        note: 'When you see a value reported to many decimal places, ask: "What instrument measured this, and what is its resolution?" If the answer is a plate reader with 5% CV, then 3 significant figures is the maximum defensible precision.',
      },
      {
        heading: 'How Adroit Enforces This',
        body: 'The rendering layer applies formatting functions that match the property type. Integer properties use **Math.round()**. Floating-point properties use **toPrecision()** or **toFixed()** depending on whether the rule is "significant figures" (IC50) or "decimal places" (MW). These rules are applied consistently across the data grid, compound cards, export CSV, and workbench result panels.',
      },
    ],
  },

  // =========================================================================
  // 2. Unit Concepts
  // =========================================================================
  'unit-concepts': {
    id: 'unit-concepts',
    title: 'Units in Drug Discovery',
    icon: '\u0394',
    brief: 'Why units matter and how Adroit handles them',
    sections: [
      {
        heading: 'The Problem',
        body: 'Drug discovery data comes from dozens of assays, each reporting in different units. One lab measures IC50 in **nM**, another in **\u00B5M**, a third in **pIC50** (negative log molar). A molecular weight might be in **Da**, **g/mol**, or **kDa**. Without unit tracking, comparing compounds across datasets is impossible — and errors are invisible.',
      },
      {
        heading: 'Concentration Units',
        body: 'Bioactivity data (IC50, EC50, Ki, Kd) is reported on a molar concentration scale. The commonly used prefixes span nine orders of magnitude.',
        table: {
          headers: ['Unit', 'Full Name', 'Value in Molar', 'Typical Use'],
          rows: [
            ['M', 'Molar', '1', 'Buffer concentrations'],
            ['mM', 'Millimolar', '10\u207B\u00B3', 'Fragment screening hits'],
            ['\u00B5M', 'Micromolar', '10\u207B\u2076', 'HTS hits, early leads'],
            ['nM', 'Nanomolar', '10\u207B\u2079', 'Optimised leads, clinical candidates'],
            ['pM', 'Picomolar', '10\u207B\u00B9\u00B2', 'Antibody-antigen binding'],
          ],
        },
      },
      {
        heading: 'Logarithmic Scales',
        body: 'The **pIC50** is defined as the negative base-10 logarithm of the IC50 in molar units. A pIC50 of 8.0 corresponds to an IC50 of 10 nM. Higher pIC50 = more potent. The logarithmic scale compresses the huge dynamic range of potency values and makes SAR trends more visible.',
        formula: 'pIC50 = -log\u2081\u2080(IC50 in M)\nExample: IC50 = 10 nM = 10\u207B\u2078 M \u2192 pIC50 = 8.0',
      },
      {
        heading: 'Adroit Unit Handling',
        body: 'Every numeric property in Adroit has an associated **canonical unit** defined by the tenant administrator. When data is imported, the field mapper detects the source unit and applies a conversion factor. All internal storage is in the canonical unit. Display can be toggled between canonical and original.',
      },
    ],
  },

  // =========================================================================
  // 3. Unit Conversion
  // =========================================================================
  'unit-conversion-how': {
    id: 'unit-conversion-how',
    title: 'How Unit Conversion Works',
    icon: '\u21C4',
    brief: 'Automatic detection, conversion factors, and the canonical unit system',
    sections: [
      {
        heading: 'The Conversion Pipeline',
        body: 'When a file is imported, each column passes through the **Field Mapper**. The mapper identifies the property type (e.g. IC50) and detects the source unit from column headers, metadata, or explicit user selection. It then applies the appropriate conversion.',
      },
      {
        heading: 'Concentration Conversions',
        body: 'All concentration values are stored internally in **nanomolar (nM)** as the canonical unit. Conversions between linear units are simple multiplications.',
        table: {
          headers: ['From', 'To nM', 'Multiply By'],
          rows: [
            ['M', 'nM', '10\u2079'],
            ['mM', 'nM', '10\u2076'],
            ['\u00B5M', 'nM', '10\u00B3'],
            ['nM', 'nM', '1'],
            ['pM', 'nM', '10\u207B\u00B3'],
            ['pIC50', 'nM', '10\u2079\u207B\u1D56\u1D35\u1D9C\u2085\u2080'],
          ],
        },
        formula: 'IC50 (nM) = 10\u2079 \u00D7 10\u207B\u1D56\u1D35\u1D9C\u2085\u2080 = 10\u207B\u1D56\u1D35\u1D9C\u2085\u2080\u207A\u2079',
      },
      {
        heading: 'Mass & Molecular Weight',
        body: 'Molecular weight is stored in **Da** (Daltons, equivalent to g/mol). For proteins and biologics, kDa is common in display but Da is canonical internally.',
        table: {
          headers: ['From', 'To Da', 'Multiply By'],
          rows: [
            ['g/mol', 'Da', '1'],
            ['kDa', 'Da', '1000'],
            ['MDa', 'Da', '10\u2076'],
          ],
        },
      },
      {
        heading: 'What Happens on Export',
        body: 'When exporting data (CSV, SDF, XLSX), values are written in the **canonical unit** by default. The column header includes the unit, e.g. "IC50 (nM)". Tenant field defaults determine which unit is canonical for each property.',
      },
    ],
  },

  // =========================================================================
  // 4. Bioactivity Types
  // =========================================================================
  'bioactivity-types': {
    id: 'bioactivity-types',
    title: 'Bioactivity Measurements',
    icon: '\u2295',
    brief: 'IC50, EC50, Ki, Kd and what they actually measure',
    sections: [
      {
        heading: 'Overview',
        body: 'Bioactivity data describes how strongly a compound interacts with a biological target. Different assay types measure different aspects of this interaction. Understanding which value to compare is critical for SAR.',
      },
      {
        heading: 'Common Bioactivity Types',
        body: 'Each measurement type has specific meaning and is not directly interchangeable with others.',
        table: {
          headers: ['Type', 'Full Name', 'What It Measures', 'Lower = ?'],
          rows: [
            ['IC50', 'Half-maximal Inhibitory Concentration', 'Concentration that inhibits 50% of target activity', 'More potent'],
            ['EC50', 'Half-maximal Effective Concentration', 'Concentration producing 50% of max effect', 'More potent'],
            ['Ki', 'Inhibition Constant', 'Thermodynamic binding affinity of inhibitor', 'Tighter binding'],
            ['Kd', 'Dissociation Constant', 'Equilibrium dissociation constant', 'Tighter binding'],
            ['GI50', 'Growth Inhibition 50%', 'Concentration inhibiting 50% cell growth', 'More cytostatic'],
            ['MIC', 'Minimum Inhibitory Concentration', 'Lowest concentration preventing growth', 'More active'],
          ],
        },
      },
      {
        heading: 'IC50 vs Ki',
        body: '**IC50 is assay-dependent** — it changes with substrate concentration, incubation time, and assay format. **Ki is assay-independent** — it reflects the true binding affinity. The Cheng-Prusoff equation relates them:',
        formula: 'Ki = IC50 / (1 + [S]/Km)',
        note: 'Always record which assay type produced the value. Comparing IC50 values from different assay formats is scientifically questionable.',
      },
      {
        heading: 'Logarithmic Forms',
        body: 'The **p-scale** (pIC50, pEC50, pKi, pKd) converts to negative log\u2081\u2080 of the molar value. A pIC50 of 9 means IC50 = 1 nM. Higher p-values = more potent. The p-scale linearises dose-response relationships and normalises the huge dynamic range.',
      },
    ],
  },

  // =========================================================================
  // 5. Patent Zones
  // =========================================================================
  'patent-zones': {
    id: 'patent-zones',
    title: 'Patent IP Zones',
    icon: '\u26A0',
    brief: 'How Adroit classifies patent collision risk',
    sections: [
      {
        heading: 'Overview',
        body: 'The Patent IP Map compares your compounds against **28.4 million** patent structures using Tanimoto similarity on Morgan fingerprints (radius 2, 512 bits). Each compound is assigned a **zone** based on its closest patent match.',
      },
      {
        heading: 'Zone Classification',
        body: 'Zones are colour-coded by risk level:',
        table: {
          headers: ['Zone', 'Similarity', 'Colour', 'Interpretation'],
          rows: [
            ['Zone 1', '\u2265 0.95', 'Red', 'Near-identical to patented structure — very high risk'],
            ['Zone 2', '0.85 \u2013 0.94', 'Orange', 'Highly similar — likely covered by Markush claims'],
            ['Zone 3', '0.70 \u2013 0.84', 'Yellow', 'Moderate similarity — may be defensible with differentiation'],
            ['Zone 4', '0.50 \u2013 0.69', 'Blue', 'Low similarity — generally safe with reasonable arguments'],
            ['Zone 5', '< 0.50', 'Green', 'Dissimilar — low patent risk'],
          ],
        },
      },
      {
        heading: 'How Similarity Is Computed',
        body: 'The pipeline uses **RDKit Morgan fingerprints** (ECFP4 equivalent) with radius 2 and 512 bits to match the PostgreSQL RDKit cartridge patent index. Tanimoto similarity is computed between your compound and every patent structure in the database.',
        formula: 'Tanimoto(A, B) = |A \u2229 B| / |A \u222A B|',
        note: 'Tanimoto similarity on Morgan fingerprints is the industry standard for patent FTO analysis, but it is a structural metric only. The patent similarity fingerprint is deliberately enantiomer-blind to match the RDKit cartridge index, so stereoisomers can have identical similarity scores. It does not consider biological activity, formulation claims, or method-of-use patents.',
      },
      {
        heading: 'Exact vs Similar Matches',
        body: '**Exact matches** (patent_count_exact) use InChIKey comparison — the compound is identical to a patented structure. **Similar matches** (patent_count_similar) use the Tanimoto threshold. Both counts and the closest patent ID are stored per molecule and included in CSV exports.',
      },
    ],
  },

  // =========================================================================
  // 6. Structure Search
  // =========================================================================
  'structure-search': {
    id: 'structure-search',
    title: 'Structure Search Methods',
    icon: '\u2315',
    brief: 'Substructure, similarity, and exact match — how they work',
    sections: [
      {
        heading: 'Three Search Types',
        body: 'Adroit supports three structure search methods, each using different algorithms and returning different result sets.',
        table: {
          headers: ['Method', 'Query Format', 'Algorithm', 'Use Case'],
          rows: [
            ['Exact Match', 'SMILES', 'Canonical SMILES comparison', 'Find a specific known compound'],
            ['Substructure', 'SMILES or SMARTS', 'VF2 subgraph isomorphism', 'Find all compounds containing a motif'],
            ['Similarity', 'SMILES + threshold', 'Tanimoto on Morgan FP', 'Find structurally related compounds'],
          ],
        },
      },
      {
        heading: 'Substructure Search',
        body: 'Substructure search finds all molecules that **contain** the query as a subgraph. The query can be a SMILES string (interpreted as a specific fragment) or a **SMARTS** pattern (with wildcards and logic). Matching is performed by the **Indigo substructure matcher** running in the platform-api service.',
        note: 'SMARTS patterns like [#6]~[#7] match any carbon bonded to any nitrogen. Use SMARTS for pharmacophore-style queries; use SMILES for exact fragment matching.',
      },
      {
        heading: 'Similarity Search',
        body: 'Similarity search computes **Tanimoto coefficient** between Morgan fingerprints of the query and every molecule in the dataset. Results are returned above the specified threshold (default 0.7), sorted by descending similarity.',
        formula: 'Tanimoto(A, B) = |A \u2229 B| / |A \u222A B|\n\nMorgan FP: radius=2, nBits=512 (ECFP4 equivalent)',
        note: 'Similarity fingerprints do not encode chirality in the canonical platform setting. Substructure stereo controls are evaluated on a separate matching path and should not be read as a guarantee that Tanimoto similarity distinguishes enantiomers.',
      },
      {
        heading: 'Performance',
        body: 'Searches are performed by **RDKit and Indigo** in the platform-api service: RDKit handles Morgan fingerprints for similarity (Tanimoto) and InChIKey-based exact matches; Indigo handles substructure (SMARTS) matching.',
      },
    ],
  },
  'entity-classification': {
    id: 'entity-classification',
    title: 'How Adroit Classifies Molecules',
    icon: '\uD83E\uDDEC',
    brief: 'Entity type auto-classification and manual override',
    sections: [
      {
        heading: 'Overview',
        body: 'Adroit automatically classifies every molecule into an **entity type** at import time. This determines which Workbench tools, ADMET models, and analysis workflows are shown for each compound. Entity types include: small molecule, peptide, protein, antibody, ADC, nucleotide, oligonucleotide, plasmid, polymer, and macrocycle.',
      },
      {
        heading: 'Classification Decision Tree',
        body: 'The classifier checks data in this priority order:\n\n1. **HELM notation** \u2014 PEPTIDE+CHEM sections indicate ADC; RNA sections indicate oligonucleotide; PEPTIDE-only checks residue count for peptide vs protein.\n\n2. **Sequence data** \u2014 Nucleotide alphabet (ATCGU) classifies by length: <100 bases = oligonucleotide, >2000 = plasmid. Amino acid sequences check for CDR3 motifs (antibody), >50 residues (protein), or shorter (peptide).\n\n3. **SMILES/Structure** \u2014 MW > 5000 suggests protein. Largest ring > 12 atoms with MW > 500 indicates macrocycle. MW > 900 with multiple peptide bonds suggests peptide.\n\n4. **Name hints** (lowest confidence) \u2014 *mab suffix = antibody, ADC/conjugate keywords, siRNA/ASO keywords, PEG/polymer keywords.',
      },
      {
        heading: 'Confidence Levels',
        body: 'Each classification includes a confidence score:\n\n- **0.85\u20130.95**: High confidence \u2014 HELM/sequence data or clear structural features\n- **0.70\u20130.80**: Medium confidence \u2014 MW/structure-based inference\n- **0.55\u20130.65**: Low confidence \u2014 Name-based hints only\n\nDefault classification (small molecule) has 0.95 confidence.',
      },
      {
        heading: 'Entity Types',
        body: '| Type | Description |\n|------|-------------|\n| **Small Molecule** | Traditional drug-like compounds (MW < 900) |\n| **Peptide** | Short amino acid chains (2\u201350 residues) |\n| **Protein** | Large polypeptides (>50 residues or MW > 5000) |\n| **Antibody** | Immunoglobulins with CDR3 motifs |\n| **ADC** | Antibody-drug conjugates (peptide + chemical linker) |\n| **Nucleotide** | Single nucleotides or short sequences |\n| **Oligonucleotide** | siRNA, ASO, antisense (<100 bases) |\n| **Plasmid** | Large circular DNA (>2000 bases) |\n| **Polymer** | PEG, polyethylene, repeating units |\n| **Macrocycle** | Large ring systems (>12 atoms, MW > 500) |',
      },
      {
        heading: 'Manual Override',
        body: 'Right-click the **Type** column in the molecule grid to manually change a molecule\'s entity type. Overrides are logged in the audit trail. Use the backfill endpoint (`POST /api/admin/backfill-entity-types`) to reclassify all existing molecules.',
      },
    ],
  },
};

export default HELP_TOPICS;
